import React, { useMemo } from 'react';
import { safeParseFloat } from 'libs/end-to-end/src/math';
import { TableViewModeArguments } from 'libs/end-to-end/src/custom-viz/TableViewTypes';
import { useGetColumnDisplayName } from 'apps/sigma2-ui/src/explore-section/hooks/useGetColumnDisplayName';
import { S2GroupedBarChart } from 'apps/sigma2-ui/src/components/S2GroupedBarChart';
import { determineUnitOfMetric } from 'libs/end-to-end/src/metrics';
import { BottomLineLayer } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GroupedBarChartMode';
import { useMetricConfig } from 'apps/sigma2-ui/src/lib/hooks/useMetricConfig';
import { SingleMetricTooltipContent } from 'apps/sigma2-ui/src/explore-section/components/TableViewModes/GenericTableViewTooltip';
import { BarTotalPopoverLayer } from 'apps/sigma2-ui/src/components/BarTotalPopoverLayer';
import { DefaultOverallDisplayMode } from 'libs/end-to-end/src/view-configurations';
import { computeValue } from 'apps/sigma2-ui/src/explore-section/utils/TableViewUtils';

/**
 * Horizontal bar chart component for visualizing TableView data using Nivo.
 *
 * Features:
 * - Converts table data to Nivo horizontal bar chart format
 * - Horizontal layout - better for long category names
 * - Custom tooltip with formatted values
 * - Consistent theming with app design system
 * - First column used as category, selected metric as value
 *
 * @example
 * ```tsx
 * <HorizontalBarChartMode
 *   data={tableData}
 *   visibleRows={visibleRows}
 *   viewConfiguration={viewConfiguration}
 * />
 * ```
 */
/**
 * Truncate long labels to avoid overlap with Y-axis legend
 * Max length calculated to fit comfortably in the left margin space
 */
export const HorizontalBarChartMode: React.FC<TableViewModeArguments> = ({
  data,
  visibleRows,
  viewConfiguration,
}) => {
  const { getColumnDisplayName } = useGetColumnDisplayName();
  const { getMetricAndFocusAreaFromColumnName } = useMetricConfig();
  const { selectedMetric } = viewConfiguration;
  const { selectedGroupBy } = data;

  const metricInfo = useMemo(() => {
    if (!selectedMetric) {
      return null;
    }

    return getMetricAndFocusAreaFromColumnName({
      columnName: selectedMetric,
    });
  }, [selectedMetric, getMetricAndFocusAreaFromColumnName]);

  const metricUnits = useMemo(() => {
    if (viewConfiguration.units) {
      return viewConfiguration.units;
    }

    if (!metricInfo) {
      return 'count';
    }

    return determineUnitOfMetric({
      knownMaximum: null,
      isMetricBoolean: metricInfo.metric.isMetricBoolean,
      metricFocusArea: metricInfo.focusArea,
      definitionUnit: metricInfo.metric.unit,
    });
  }, [metricInfo, viewConfiguration.units]);

  const units = useMemo(() => {
    return viewConfiguration.overallDisplayMode === 'count'
      ? 'count'
      : metricUnits;
  }, [viewConfiguration.overallDisplayMode, metricUnits]);

  const xGroups = useMemo(() => {
    if (!selectedMetric) {
      return [];
    }

    const selectedMetricIndex = data.columnHeaders.indexOf(selectedMetric);
    if (selectedMetricIndex === -1) {
      return [];
    }

    const barChartData = data.rows.map((row, rowIndex) => {
      const categoryValue = row[0]; // First column is the category (groupBy dimension)
      const metricValue = row[selectedMetricIndex + 1]; // +1 because row[0] is groupBy dimension
      const numericValue = safeParseFloat(metricValue) || 0;

      return {
        id: categoryValue,
        label: categoryValue,
        groupLevels: [
          {
            id: selectedMetric,
            value: computeValue({
              value: numericValue,
              numeratorSize:
                data.numeratorSizes[rowIndex]?.[selectedMetricIndex + 1] ?? 0,
              chartUnits: units,
              overallDisplayMode: viewConfiguration.overallDisplayMode,
            }),
            tooltip: (
              <SingleMetricTooltipContent
                metricColumn={selectedMetric}
                percentageValue={numericValue}
                levelDisplayName={categoryValue}
                totalSubjects={
                  data.sampleSizes[rowIndex][selectedMetricIndex + 1]
                }
                numeratorSize={
                  data.numeratorSizes[rowIndex]?.[selectedMetricIndex + 1] ?? 0
                }
                units={metricUnits}
                overallDisplayMode={viewConfiguration.overallDisplayMode}
              />
            ),
          },
        ],
      };
    });

    const filteredBarChartData = barChartData
      .filter(
        (row, index) => visibleRows.length === 0 || visibleRows.includes(index),
      )
      .reverse();

    return filteredBarChartData;
  }, [
    selectedMetric,
    data.columnHeaders,
    data.rows,
    data.numeratorSizes,
    data.sampleSizes,
    units,
    viewConfiguration.overallDisplayMode,
    metricUnits,
    visibleRows,
  ]);

  if (!selectedMetric) {
    return null;
  }

  return (
    <div className="w-full bg-white">
      <S2GroupedBarChart
        xGroups={xGroups}
        xLevels={[
          {
            id: selectedMetric,
            label: selectedMetric,
          },
        ]}
        units={units}
        yAxisLabel={
          selectedGroupBy
            ? getColumnDisplayName(selectedGroupBy)
            : (metricInfo?.metric.displayName ?? '')
        }
        verticalGroupPaddingAsFactorOfHeight={0.3}
        horizontalMinBarWidth={Infinity}
        verticalBarFixedThickness={true}
        verticalMinHeight={380}
        verticalMaxHeight={400}
        hoverEffect={false}
        margin={{ top: 50, right: 50, bottom: 50, left: 170 }}
        enableGridX={false}
        enableGridY={true}
        markers={[
          {
            axis: 'x',
            value: 0,
            lineStyle: {
              stroke: '#b8babc',
              strokeWidth: '1px',
            },
          },
        ]}
        layers={[BottomLineLayer, BarTotalPopoverLayer]}
        axisBottom={{
          tickSize: 4,
          tickPadding: 6,
        }}
      />
    </div>
  );
};
